import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useAccount } from "../../context/AccountContext";

const STATUS_META = {
  PENDING:   { label: "Pending",   color: "#fd7e14", bg: "#fff3cd" },
  EXECUTING: { label: "Executing", color: "#6f42c1", bg: "#e8d5ff" },
  COMPLETED: { label: "Completed", color: "#28a745", bg: "#d4edda" },
  FAILED:    { label: "Failed",    color: "#dc3545", bg: "#fde2e2" },
};

const SIDE_COLOR = { BUY: "#28a745", SELL: "#dc3545" };
const DAYS_OPTIONS = [1, 3, 7, 30];

export default function OrderQueuePage() {
  const { selectedAccount } = useAccount();
  const isAllAccounts = selectedAccount === "All Accounts";

  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter]   = useState("ALL");
  const [days, setDays]       = useState(7);
  const [msg, setMsg]         = useState(null);

  const flash = (text, ok = true) => {
    setMsg({ text, ok });
    setTimeout(() => setMsg(null), 3000);
  };

  const fetchOrders = useCallback(async () => {
    if (!selectedAccount) return;
    setLoading(true);
    try {
      const params = { username: selectedAccount, days, limit: 300 };
      if (filter !== "ALL") params.status = filter;
      const res = await axios.get("/automation/orders", { params });
      setRows(res.data);
    } catch (e) {
      console.error(e);
      flash("Failed to load orders.", false);
    } finally {
      setLoading(false);
    }
  }, [selectedAccount, filter, days]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const counts = rows.reduce((acc, r) => {
    acc[r.status] = (acc[r.status] || 0) + 1;
    return acc;
  }, {});

  return (
    <div style={{ padding: "20px 25px 40px", background: "#f4f6f9", minHeight: "100vh" }}>

      {msg && (
        <div style={{ ...toast, background: msg.ok ? "#28a745" : "#dc3545" }}>
          {msg.text}
        </div>
      )}

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
        <div>
          <h2 style={pageTitle}>Order Queue</h2>
          <p style={{ color: "#888", fontSize: 13, margin: 0 }}>
            Orders generated by action handlers — submitted to broker automatically
          </p>
        </div>
        <button onClick={fetchOrders} style={refreshBtn} disabled={loading}>
          {loading ? "Loading…" : "Refresh"}
        </button>
      </div>

      {/* Summary tiles */}
      <div style={statsGrid}>
        {[["Total", rows.length, "#2c3e50"],
          ["Pending",   counts.PENDING   || 0, "#fd7e14"],
          ["Executing", counts.EXECUTING || 0, "#6f42c1"],
          ["Completed", counts.COMPLETED || 0, "#28a745"],
          ["Failed",    counts.FAILED    || 0, "#dc3545"],
        ].map(([label, value, color]) => (
          <div key={label} style={{ ...tile, boxShadow: label === "Pending" && value > 0 ? `0 0 0 2px ${color}` : tile.boxShadow }}>
            <div style={{ fontSize: 11, color: "#888", textTransform: "uppercase", marginBottom: 4 }}>{label}</div>
            <div style={{ fontSize: 20, fontWeight: 700, color }}>{value}</div>
          </div>
        ))}
      </div>

      {/* Controls */}
      <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
        <div style={filterBar}>
          {["ALL", "PENDING", "EXECUTING", "COMPLETED", "FAILED"].map(s => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              style={{
                ...filterTab,
                background:  filter === s ? "#2c3e50" : "#fff",
                color:       filter === s ? "#fff"    : "#555",
                borderColor: filter === s ? "#2c3e50" : "#dee2e6",
              }}
            >
              {s === "ALL" ? `All (${rows.length})` : `${s} (${counts[s] || 0})`}
            </button>
          ))}
        </div>

        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontSize: 12, color: "#888" }}>Show last</span>
          {DAYS_OPTIONS.map(d => (
            <button key={d} onClick={() => setDays(d)} style={{
              ...filterTab, padding: "5px 10px",
              background:  days === d ? "#2c3e50" : "#fff",
              color:       days === d ? "#fff"    : "#555",
              borderColor: days === d ? "#2c3e50" : "#dee2e6",
            }}>{d}d</button>
          ))}
        </div>
      </div>

      {/* Table */}
      {loading && rows.length === 0 ? (
        <div style={emptyBox}>Loading orders…</div>
      ) : rows.length === 0 ? (
        <div style={emptyBox}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>📋</div>
          <div style={{ fontWeight: 600, color: "#555" }}>No orders yet</div>
          <div style={{ fontSize: 13, color: "#999", marginTop: 6 }}>
            Orders appear here once an approved action is executed
          </div>
        </div>
      ) : (
        <div style={{ background: "#fff", borderRadius: 12, boxShadow: "0 2px 8px rgba(0,0,0,0.06)", overflow: "hidden" }}>
          {/* Header */}
          <div style={tableHeader}>
            <div style={{ flex: "0 0 90px" }}>Side</div>
            <div style={{ flex: 1 }}>Symbol</div>
            {isAllAccounts && <div style={{ flex: "0 0 110px" }}>Account</div>}
            <div style={{ flex: "0 0 80px", textAlign: "right" }}>Qty</div>
            <div style={{ flex: "0 0 90px", paddingLeft: 12 }}>Type</div>
            <div style={{ flex: "0 0 130px" }}>Status</div>
            <div style={{ flex: "0 0 160px" }}>Strategy</div>
            <div style={{ flex: "0 0 120px" }}>Broker ID</div>
            <div style={{ flex: "0 0 150px" }}>Created</div>
            <div style={{ flex: "0 0 150px" }}>Executed</div>
          </div>

          {rows.map((row, idx) => {
            const sm = STATUS_META[row.status] || STATUS_META.PENDING;
            return (
              <div
                key={row.id}
                style={{
                  display: "flex", alignItems: "center",
                  padding: "11px 18px",
                  borderTop: idx === 0 ? "none" : "1px solid #f0f0f0",
                  background: row.status === "FAILED" ? "#fff8f8" : "transparent",
                }}
              >
                {/* Side badge */}
                <div style={{ flex: "0 0 90px" }}>
                  <span style={{
                    display: "inline-block", padding: "3px 10px", borderRadius: 8,
                    fontWeight: 700, fontSize: 12,
                    background: row.side === "BUY" ? "#d4edda" : "#fde2e2",
                    color: SIDE_COLOR[row.side] || "#555",
                  }}>
                    {row.side || "—"}
                  </span>
                </div>

                {/* Symbol */}
                <div style={{ flex: 1, fontWeight: 600, fontSize: 13, color: "#2c3e50" }}>
                  {row.symbol}
                </div>

                {/* Account — only in All Accounts mode */}
                {isAllAccounts && (
                  <div style={{ flex: "0 0 110px" }}>
                    {row.username && (
                      <span style={{ padding: "2px 8px", borderRadius: 8, background: "#e9ecef", color: "#495057", fontSize: 11, fontWeight: 600 }}>
                        {row.username}
                      </span>
                    )}
                  </div>
                )}

                {/* Qty */}
                <div style={{ flex: "0 0 80px", textAlign: "right", fontSize: 13, fontWeight: 600, color: "#555" }}>
                  {row.quantity?.toLocaleString()}
                </div>

                {/* Order type */}
                <div style={{ flex: "0 0 90px", fontSize: 12, color: "#888", paddingLeft: 12 }}>
                  {row.order_type || "MARKET"}
                </div>

                {/* Status */}
                <div style={{ flex: "0 0 130px" }}>
                  <span style={{ ...statusBadge, background: sm.bg, color: sm.color }}>
                    {sm.label}
                  </span>
                  {row.error_message && (
                    <div style={{ fontSize: 11, color: "#dc3545", marginTop: 2 }}>
                      {row.error_message.slice(0, 50)}
                    </div>
                  )}
                </div>

                {/* Strategy */}
                <div style={{ flex: "0 0 160px", fontSize: 12, color: "#888" }}>
                  {row.strategy_name || "—"}
                </div>

                {/* Broker order id */}
                <div style={{ flex: "0 0 120px", fontSize: 11, fontFamily: "monospace" }}>
                  {row.broker_order_id
                    ? row.broker_order_id.startsWith("PAPER-")
                      ? <span style={{ color: "#fd7e14", background: "#fff3cd", padding: "2px 6px", borderRadius: 4, fontWeight: 600 }}>
                          PAPER
                        </span>
                      : <span style={{ color: "#2c3e50" }}>{row.broker_order_id}</span>
                    : <span style={{ color: "#aaa" }}>—</span>
                  }
                </div>

                {/* Created */}
                <div style={{ flex: "0 0 150px", fontSize: 12, color: "#888" }}>
                  {row.created_at?.slice(0, 16)}
                </div>

                {/* Executed */}
                <div style={{ flex: "0 0 150px", fontSize: 12, color: "#888" }}>
                  {row.executed_at?.slice(0, 16) || "—"}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ── Styles ─────────────────────────────────── */

const pageTitle  = { fontSize: 22, fontWeight: 700, color: "#2c3e50", margin: 0 };
const toast      = { position: "fixed", top: 20, right: 20, zIndex: 9999, padding: "10px 20px", borderRadius: 8, color: "#fff", fontWeight: 600, fontSize: 14 };
const refreshBtn = { padding: "8px 16px", borderRadius: 8, border: "1px solid #ccc", background: "#fff", cursor: "pointer", fontSize: 13 };
const statsGrid  = { display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 12, marginBottom: 20 };
const tile       = { background: "#fff", borderRadius: 10, padding: "14px 16px", boxShadow: "0 2px 8px rgba(0,0,0,0.05)" };
const filterBar  = { display: "flex", gap: 6, flexWrap: "wrap" };
const filterTab  = { padding: "6px 12px", borderRadius: 20, border: "1px solid", cursor: "pointer", fontSize: 12, fontWeight: 500 };
const emptyBox   = { background: "#fff", borderRadius: 12, padding: "60px 20px", textAlign: "center", color: "#888", boxShadow: "0 2px 10px rgba(0,0,0,0.05)" };
const tableHeader = { display: "flex", alignItems: "center", padding: "10px 18px", background: "#f8f9fb", borderBottom: "1px solid #eee", fontSize: 11, fontWeight: 600, color: "#888", textTransform: "uppercase", letterSpacing: "0.5px" };
const statusBadge = { display: "inline-block", padding: "3px 10px", borderRadius: 12, fontSize: 12, fontWeight: 600 };
